"use client";

import { useMemo } from "react";
import { GroupedVirtuoso, Virtuoso } from "react-virtuoso";
import { VirtuosoErrorBoundary } from "@/components/virtuoso-error-boundary";
import { cn } from "@/lib/utils";
import type { BoardView, BookId, FamilyId, FamilySummary, LeagueId, MarketSpec, OddsDisplay, RowId } from "@/lib/odds-screen/live/types";
import { LiveGameRow, LiveSubjectRow, type CellTarget, type HiddenRow } from "./board-rows";
import { hasPlayOrPeriods } from "./live-state";
import type { BetActions } from "./bet";
import { EventGroupHeader, KickoffHeader } from "./group-headers";
import { ScrollFade } from "./top-menus";
import { marketDisplayOrder } from "./view-model";
import { outcomeDisplayOrder, outcomeName } from "./labels";
import { countNoun, eventGroups, eventItems, starredKickoffGroups, subjectCols, subjectShape, type CountNoun } from "./subject";
import { kickoffGroups, kickoffKeys } from "./time";
import { useStars } from "./star-button";

type Drill = { readonly rowId: RowId; readonly slot: number; readonly outcome: number } | null;

function linesGridCols(count: number): string {
  return `minmax(260px,1fr) repeat(${count}, minmax(140px,240px))`;
}

const LABEL = "pl-2 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500";

export function DayFilterBar({ children }: { children: React.ReactNode }) {
  return (
    <ScrollFade className="scrollbar-hide flex h-9 flex-shrink-0 items-center gap-2 overflow-x-auto border-b border-surface-line bg-surface-chrome px-4">
      {children}
    </ScrollFade>
  );
}

export function ColumnHeader({ cols, leftLabel, labels, compact = false, className }: { cols: string; leftLabel: React.ReactNode; labels: readonly string[]; compact?: boolean; className?: string }) {
  return (
    <div className={cn("scrollbar-custom grid h-9 flex-shrink-0 items-center justify-start gap-x-2 overflow-hidden border-b border-surface-line bg-surface-chrome [scrollbar-gutter:stable]", compact ? "px-3" : "px-4", className)} style={{ gridTemplateColumns: cols }}>
      <div className="flex min-w-0 items-center">{leftLabel}</div>
      {labels.map((label, index) => <span key={index} className={cn(LABEL, compact && "pl-0 text-center")}>{label}</span>)}
    </div>
  );
}

export function LinesBoard({
  league, family, specs, view, rowIds, selected, format, railOpen, compact = false, wide = false, showDate, drill, filters, bet, onOpen, onHide, onOpenGame,
}: {
  league: LeagueId;
  family: FamilyId;
  specs: readonly MarketSpec[];
  view: BoardView;
  rowIds: readonly RowId[];
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  railOpen: boolean;
  compact?: boolean;
  wide?: boolean;
  showDate: boolean;
  drill: Drill;
  filters: React.ReactNode;
  bet: BetActions;
  onOpen: (rowId: RowId, target: CellTarget) => void;
  onHide: (row: HiddenRow) => void;
  onOpenGame: (rowId: RowId) => void;
}) {
  const order = useMemo(() => marketDisplayOrder(specs), [specs]);
  const cols = linesGridCols(order.length);
  const stars = useStars();
  const groups = useMemo(() => starredKickoffGroups(view, rowIds, stars), [view, rowIds, stars]);
  const shown = useMemo(() => groups.flatMap((group) => group.rowIds), [groups]);
  const counts = useMemo(() => groups.map((group) => group.rowIds.length), [groups]);
  const keys = useMemo(() => kickoffKeys({ counts, starts: groups.map((group) => group.startMs) }, shown), [counts, groups, shown]);
  const playColumn = useMemo(() => rowIds.some((rowId) => {
    const ref = view.rows.get(rowId);
    return hasPlayOrPeriods(ref && view.events.get(ref.eventId)?.live);
  }), [rowIds, view]);

  return (
    <div className="flex min-h-0 flex-grow flex-col">
      {filters ? <DayFilterBar>{filters}</DayFilterBar> : null}
      <ColumnHeader cols={cols} className="pl-7" leftLabel={<span className={cn(LABEL, "pl-1")}>Teams</span>} labels={order.map((slot) => specs[slot].label)} />
      <VirtuosoErrorBoundary>
        <GroupedVirtuoso
          className="scrollbar-custom scrollbar-color-primary [scrollbar-gutter:stable]"
          style={{ height: "100%", width: "100%" }}
          groupCounts={counts}
          computeItemKey={(index) => keys[index] ?? index}
          groupContent={(groupIndex) => <KickoffHeader startMs={groups[groupIndex]?.startMs || null} showDate={showDate} starred={groups[groupIndex]?.starred} />}
          itemContent={(index) => {
            const rowId = shown[index];
            const ref = view.rows.get(rowId);
            const event = ref ? view.events.get(ref.eventId) : undefined;
            return (
              <LiveGameRow
                league={league}
                family={family}
                rowId={rowId}
                event={event}
                specs={specs}
                order={order}
                selected={selected}
                format={format}
                gridCols={cols}
                drillSlot={drill?.rowId === rowId ? drill.slot : null}
                drillOutcome={drill?.rowId === rowId ? drill.outcome : null}
                rowSelected={drill?.rowId === rowId}
                compact={compact}
                wide={wide}
                playColumn={playColumn}
                bet={bet}
                onOpen={onOpen}
                onHide={onHide}
                onOpenGame={onOpenGame}
              />
            );
          }}
        />
      </VirtuosoErrorBoundary>
    </div>
  );
}

function EventList({ league, view, rowIds, collapsed, compact, noun, renderRow, onToggle }: {
  league: LeagueId;
  view: BoardView;
  rowIds: readonly RowId[];
  collapsed: ReadonlySet<string>;
  compact: boolean;
  noun: CountNoun;
  renderRow: (rowId: RowId) => React.ReactNode;
  onToggle: (eventId: string) => void;
}) {
  const items = useMemo(() => eventItems(view, rowIds, collapsed, compact, league), [view, rowIds, collapsed, compact, league]);
  return (
    <Virtuoso
      className="scrollbar-custom scrollbar-color-primary [scrollbar-gutter:stable]"
      style={{ height: "100%", width: "100%" }}
      data={items}
      increaseViewportBy={{ top: 200, bottom: 200 }}
      computeItemKey={(_, item) => (item.kind === "event" ? `g:${item.eventId}` : `r:${item.rowId}`)}
      itemContent={(_, item) => (
        item.kind === "event"
          ? <EventGroupHeader label={item.label} time={item.time} count={item.count} noun={noun} collapsed={item.collapsed} compact={compact} onToggle={() => onToggle(item.eventId)} />
          : renderRow(item.rowId)
      )}
    />
  );
}

function StickyEventList({ league, view, rowIds, collapsed, showDate, noun, renderRow, onToggle }: {
  league: LeagueId;
  view: BoardView;
  rowIds: readonly RowId[];
  collapsed: ReadonlySet<string>;
  showDate: boolean;
  noun: CountNoun;
  renderRow: (rowId: RowId) => React.ReactNode;
  onToggle: (eventId: string) => void;
}) {
  const groups = useMemo(() => eventGroups(view, rowIds, collapsed, "full", showDate, league), [view, rowIds, collapsed, showDate, league]);
  const shown = useMemo(() => groups.flatMap((group) => (group.collapsed ? [] : group.rowIds)), [groups]);
  const counts = useMemo(() => groups.map((group) => (group.collapsed ? 0 : group.rowIds.length)), [groups]);
  const keys = useMemo(() => kickoffKeys({ counts, starts: groups.map((group) => group.startMs) }, shown), [counts, groups, shown]);
  return (
    <GroupedVirtuoso
      className="scrollbar-custom scrollbar-color-primary [scrollbar-gutter:stable]"
      style={{ height: "100%", width: "100%" }}
      groupCounts={counts}
      computeItemKey={(index) => keys[index] ?? index}
      groupContent={(groupIndex) => {
        const group = groups[groupIndex];
        return <EventGroupHeader label={group.label} date={group.date} time={group.time} count={group.rowIds.length} noun={noun} collapsed={group.collapsed} compact={false} onToggle={() => onToggle(group.eventId)} />;
      }}
      itemContent={(index) => renderRow(shown[index])}
    />
  );
}

function KickoffList({ view, rowIds, showDate, renderRow }: {
  view: BoardView;
  rowIds: readonly RowId[];
  showDate: boolean;
  renderRow: (rowId: RowId) => React.ReactNode;
}) {
  const groups = useMemo(() => kickoffGroups(rowIds.map((rowId) => {
    const ref = view.rows.get(rowId);
    return (ref ? view.events.get(ref.eventId)?.startMs : undefined) ?? 0;
  })), [rowIds, view]);
  const keys = useMemo(() => kickoffKeys(groups, rowIds), [groups, rowIds]);
  return (
    <GroupedVirtuoso
      className="scrollbar-custom scrollbar-color-primary [scrollbar-gutter:stable]"
      style={{ height: "100%", width: "100%" }}
      groupCounts={groups.counts}
      computeItemKey={(index) => keys[index] ?? index}
      groupContent={(groupIndex) => <KickoffHeader startMs={groups.starts[groupIndex] || null} showDate={showDate} />}
      itemContent={(index) => renderRow(rowIds[index])}
    />
  );
}

export function SubjectBoard({
  league, family, view, rowIds, selected, format, showDate, drill, collapsed, filters, compact = false, bet, onToggle, onOpen, onHide,
}: {
  league: LeagueId;
  family: FamilySummary;
  view: BoardView;
  rowIds: readonly RowId[];
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  showDate: boolean;
  drill: Drill;
  collapsed: ReadonlySet<string>;
  filters: React.ReactNode;
  compact?: boolean;
  bet: BetActions;
  onToggle: (eventId: string) => void;
  onOpen: (rowId: RowId, target: CellTarget) => void;
  onHide: (row: HiddenRow) => void;
}) {
  const spec = family.markets[0];
  const order = useMemo(() => outcomeDisplayOrder(spec), [spec]);
  const cols = subjectCols(family, compact);
  const { grouping, heading } = subjectShape(family);
  const noun = countNoun(family);

  const renderRow = (rowId: RowId) => {
    const ref = view.rows.get(rowId);
    return (
      <LiveSubjectRow
        league={league}
        family={family}
        rowId={rowId}
        event={ref ? view.events.get(ref.eventId) : undefined}
        selected={selected}
        format={format}
        gridCols={cols}
        drillOutcome={drill?.rowId === rowId ? drill.outcome : null}
        rowSelected={drill?.rowId === rowId}
        compact={compact}
        bet={bet}
        onOpen={onOpen}
        onHide={onHide}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-grow flex-col">
      {filters ? <DayFilterBar>{filters}</DayFilterBar> : null}
      <ColumnHeader cols={cols} compact={compact} leftLabel={<span className={cn(LABEL, "pl-1")}>{heading}</span>} labels={order.map((outcome) => outcomeName(spec, undefined, outcome))} />
      <VirtuosoErrorBoundary>
        {grouping === "event" ? (
          <EventList league={league} view={view} rowIds={rowIds} collapsed={collapsed} compact={compact} noun={noun} renderRow={renderRow} onToggle={onToggle} />
        ) : grouping === "stickyEvent" ? (
          <StickyEventList league={league} view={view} rowIds={rowIds} collapsed={collapsed} showDate={showDate} noun={noun} renderRow={renderRow} onToggle={onToggle} />
        ) : (
          <KickoffList view={view} rowIds={rowIds} showDate={showDate} renderRow={renderRow} />
        )}
      </VirtuosoErrorBoundary>
    </div>
  );
}
